import React, { memo, useState, useCallback, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { RouteComponentProps } from 'react-router';
import FiltersHeader from './components/FiltersHeader';
import { StatusTag, Table } from 'components';
import { objToQuery, queryToObj } from 'utils/query';
import { routePaths } from 'constants/routes';
import { format } from 'date-fns';
import { demandStatusLabels, demandStatusVariant } from 'constants/demands';
import { getClientDemandsData, IGCDDParams } from 'services/demandsTable';
import { IClientDemandsTable } from 'types/demandsTable';
import useEventBus from 'hooks/useEventBus';
import { openNotification } from 'utils/notification';
import { getLoggedUser } from 'utils/storage';

interface IProps extends RouteComponentProps {}

interface IUserType {
  admin: boolean;
  id: number;
  name: string;
}

const ClientDemandsPage: React.FC<IProps> = ({ history }) => {
  const eventBus = useEventBus();
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'Solicitações');
  }, []);
  const [data, setData] = useState<IClientDemandsTable>({ content: [] });
  const [isLoading, setIsLoading] = useState(false);

  const handleGetData = useCallback(async (query?: Omit<IGCDDParams, 'size'>) => {
    setIsLoading(true);
    try {
      const queryData = {
        size: 10,
        page: 0,
        ...query,
        sort: 'prazo,asc',
      } as IGCDDParams;

      const response = await getClientDemandsData(queryData);
      setData(response.data);
    } catch (error) {
      openNotification('Você não possui acesso a esta função.', 'warn');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handlePaginate = (newPage: number) => {
    const newFilterData = queryToObj(history.location.search);
    const query = objToQuery({ ...newFilterData, page: Math.max(newPage - 1, 0) });
    if (
      newFilterData.page &&
      typeof newFilterData.page === 'string' &&
      newPage - 1 === parseInt(newFilterData.page)
    )
      return;
    history.replace({
      pathname: routePaths.clientDemands,
      search: `?${query}`,
    });
  };

  const getUserType = () => {
    const userType = localStorage.getItem('@dpohub/user');

    if (typeof userType === 'string') {
      const userTp: IUserType = JSON.parse(userType);

      return {
        admin: userTp.admin,
        id: userTp.id,
        name: userTp.name,
      };
    }
  };

  useEffect(() => {
    getUserType();
    // @ts-ignore
    handleGetData(queryToObj(history.location?.search || ''));
    // @ts-ignore
    const removeListen = history.listen(listener => handleGetData(queryToObj(listener.search)));

    return () => {
      removeListen();
    };
  }, [history]);

  useEffect(() => {
    const user = getLoggedUser();
    if (user.admin && !user.client) history.goBack();
  }, [history]);

  return (
    <Box w="100%">
      <FiltersHeader history={history} />
      <Box padding="0.1rem 0rem 1rem" background="#fff" borderRadius="10px">
        <Table
          enabled={[true]}
          loading={isLoading}
          row={[0]}
          id={[0]}
          headers={['Demanda', 'Modalidade', 'Criado por', 'Prazo', 'Status']}
          sizes={[1.5, 1, 0.7, 0.7, 0.5]}
          fullLoading={false}
          locations={data?.content.map(demand => '/clientDemands/details/' + demand.id)}
          rows={data?.content.map(demand => [
            demand.demanda,
            demand.modalidade,
            demand.createdBy,
            format(new Date(demand.prazo), 'dd/MM/yyyy'),
            <StatusTag
              key={demand.status}
              variant={demand.status ? demandStatusVariant[demand.status] : 'accept'}
            >
              {demand.status ? demandStatusLabels[demand.status] : '-'}
            </StatusTag>,
          ])}
          pagination={{
            onChange: handlePaginate,
            pagesCount: data?.totalPages || 1,
          }}
        />
      </Box>
    </Box>
  );
};

export const ClientDemands = memo(ClientDemandsPage);
